import { Component, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute } from "@angular/router";
import { UiKitModule } from "@app/ui-kit.module";
import { FilterProductComponent } from "@components/filter-product/filter-product.component";
import { SelectPlaceholderModalComponent } from "@components/select-placeholder-modal/select-placeholder-modal.component";
import {
  FGDS_SEARCHABLE_PRODUCT_TYPES,
  FgdsProductType,
  FullProductType,
  isRequiredAttachment,
  LocalizeProductNamePipe,
  Product,
} from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { AlarmDeviceConfiguration } from "@domain/project/configurations/alarm-device-configuration";
import { FlameDetectorConfiguration } from "@domain/project/configurations/flame-detector-configuration";
import { GasWarningCenterConfiguration } from "@domain/project/configurations/gas-warning-center-configuration";
import { PlasticSignConfiguration } from "@domain/project/configurations/plastic-sign-configuration";
import { SignalElementConfiguration } from "@domain/project/configurations/signal-element-configuration";
import { TransmitterConfiguration } from "@domain/project/configurations/transmitter-configuration";
import { FgdsProject as Project } from "@domain/project/fgds-project";
import { FloorplanPlaceholder } from "@domain/project/floorplan/floorplan-placeholder";
import { PlaceholderProduct } from "@domain/project/floorplan/placeholder-product";
import { FgdsProductListAddService } from "@domain/project/product-list/fgds-product-list-add.service";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { ModalService, ModalSize } from "@odx/angular/components/modal";
import { FgdsProductSearchItemComponent } from "@project/floorplanner/floorplan-menu/fgds/fgds-product-search-item/fgds-product-search-item.component";
import { FloorplanService } from "@project/floorplanner/floorplan.service";
import { ProductItem } from "@project/product-item";
import { combineLatest, first, map } from "rxjs";
import { createFgdsPlaceholder } from "./fgds-product-search-menu.helpers";

@Component({
  selector: "app-fgds-product-search-menu",
  templateUrl: "./fgds-product-search-menu.component.html",
  styleUrls: ["./fgds-product-search-menu.component.scss"],
  imports: [UiKitModule, FilterProductComponent, FgdsProductSearchItemComponent],
  providers: [LocalizeProductNamePipe],
  standalone: true,
})
export class FgdsProductSearchMenuComponent {
  private productService = inject(ProductService);
  private activeRoute = inject(ActivatedRoute);
  private floorplanService = inject(FloorplanService);
  private localizeProductNamePipe = inject(LocalizeProductNamePipe);
  private userService = inject(UserService);
  private projectService = inject(ProjectService);
  private fgdsProductListAddService = inject(FgdsProductListAddService);

  protected readonly canEditProductList = toSignal(
    combineLatest([this.userService.user$, this.projectService.hasUserWritePermission$]).pipe(
      map(([user, hasWritePermission]) => !user.readOnlyAccess && hasWritePermission === true),
    ),
    { initialValue: false },
  );

  private readonly project: Project;
  protected readonly floorplanProductTypes = FGDS_SEARCHABLE_PRODUCT_TYPES;
  private products: Product[] = [];
  filteredResults: Product[] = [];
  protected searchValue: string = "";

  private readonly modalService = inject(ModalService);

  constructor() {
    this.project = this.activeRoute.parent!.snapshot.data["project"];
    this.collectProducts();
  }

  protected search(value: string) {
    this.searchValue = value;
    if (!value) {
      this.filteredResults = [];
      return;
    }
    this.filteredResults = this.products.filter((product) => this.productService.searchFilter(product, value));
  }

  protected addProductToFloorplan(product: Product) {
    if (!FGDS_SEARCHABLE_PRODUCT_TYPES.includes(product.type)) return;
    let config;
    const name = this.localizeProductNamePipe.transform(product);
    const requiredAttachments: ProductItem[] = product.relationships
      .filter((relationship) => isRequiredAttachment(relationship))
      .map((relationShip) => ProductItem.create(relationShip.productId));
    const isFlameDetector =
      product.type === FgdsProductType.FLAME_DETECTOR || product.fullType === FullProductType.FLAME_DETECTOR_T_FLAME_K;
    switch (isFlameDetector ? FgdsProductType.FLAME_DETECTOR : product.type) {
      case FgdsProductType.ALARMDEVICE:
        config = AlarmDeviceConfiguration.create(this.project, [product], requiredAttachments);
        this.project.addAlarmDevices(config);
        this.floorplanService.addAlarmDevice(config);
        break;
      case FgdsProductType.GASWARNINGCENTER:
        config = GasWarningCenterConfiguration.create(name, this.project, product.id, requiredAttachments);
        this.project.addGasWarningCenters(config);
        this.floorplanService.addGasWarningCenter(config);
        break;
      case FgdsProductType.TRANSMITTER:
        config = TransmitterConfiguration.create(name, this.project, product.id, requiredAttachments);
        this.project.addTransmitters(config);
        this.floorplanService.addTransmitter(config);
        break;
      case FgdsProductType.FLAME_DETECTOR:
        config = FlameDetectorConfiguration.create(name, this.project, product.id, requiredAttachments);
        this.project.addFlameDetectors(config);
        this.floorplanService.addFlameDetector(config);
        break;
      case FgdsProductType.PLASTICSIGN:
        config = PlasticSignConfiguration.create(name, this.project, product.id, "", "", "");
        this.project.addPlasticSigns(config);
        this.floorplanService.addPlasticSign(config);
        break;
      case FgdsProductType.SIGNALELEMENT:
        config = SignalElementConfiguration.create(name, this.project, product.id);
        this.project.addSignalElements(config);
        this.floorplanService.addSignalElement(config);
        break;
      default:
        return;
    }
  }

  protected addProductToBom(product: Product) {
    if (!this.canEditProductList()) return;
    const name = this.localizeProductNamePipe.transform(product);
    this.fgdsProductListAddService.addProductToProductList(product, this.project, name);
  }

  protected openSelectPlaceholderMenu() {
    this.modalService
      .open(SelectPlaceholderModalComponent, {
        data: this.floorplanProductTypes,
        size: ModalSize.SMALL,
      })
      .onClose$.subscribe((productType) => {
        if (!productType) return;
        this.setPlaceholderProductId(createFgdsPlaceholder(productType, this.floorplanService));
      });
  }

  private collectProducts() {
    this.productService
      .getAvailableProducts()
      .pipe(
        map((products) => Product.sort(products)),
        first(),
      )
      .subscribe((products: Product[]) => (this.products = products));
  }

  private setPlaceholderProductId(placeholder: FloorplanPlaceholder) {
    placeholder.products = [new PlaceholderProduct("", this.searchValue)];
  }
}
